<template>
  <ion-app>
    <ion-router-outlet />
  </ion-app>
</template>

<script>
import { IonApp, IonRouterOutlet, toastController, useIonRouter } from '@ionic/vue';
import { defineComponent, onMounted, onUnmounted } from 'vue';

export default defineComponent({
  name: 'App',
  components: {
    IonApp,
    IonRouterOutlet
  },
  setup() {
    // Based on https://dev.to/drbragg/handling-service-worker-updates-in-your-vue-pwa-1pip
    let refreshing = false;
    let registration = null;
    let updateExists = false;
    const ionRouter = useIonRouter();

    let isUpdateExistsToastPresent = false;
    const showUpdateExistsWarning = async () => {
      if (updateExists) {
        if (!isUpdateExistsToastPresent) {
          isUpdateExistsToastPresent = true;
          const toast = await toastController
            .create({
              message: 'An update is available',
              color: 'success',
              buttons: [
                {
                  text: 'Update',
                  role: 'update',
                  handler: () => {
                    refreshApp();
                  },
                },
                {
                  text: 'Dismiss',
                  role: 'cancel'
                },
              ]
            });

          await toast.present();
          await toast.onDidDismiss().then(() => (isUpdateExistsToastPresent = false));
        }
      }
    };

    // Store the SW registration so we can send it a message
    // We use `updateExists` to control whatever alert, toast, dialog, etc we want to use
    // To alert the user there is an update they need to refresh for
    function updateAvailable(event) {
      registration = event.detail;
      updateExists = true;
      showUpdateExistsWarning();
    }

    // Called when the user accepts the update
    function refreshApp() {
      updateExists = false;
      // Make sure we only send a 'skip waiting' message if the SW is waiting
      if (!registration || !registration.waiting) return;
      // send message to SW to skip the waiting and activate the new SW
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      // go to base url after update
      ionRouter.push('/');
    }

    let isOfflineWarningToastPresent = false;
    const showOfflineWarning = async () => {
      if (!navigator.onLine) {
        if (!isOfflineWarningToastPresent) {
          isOfflineWarningToastPresent = true;
          const toast = await toastController
            .create({
              message: 'You appear to be offline. Check your network connection.',
              color: 'medium',
              buttons: [
                {
                  text: 'OK',
                  role: 'cancel'
                }
              ]
            })
          await toast.present();
          await toast.onDidDismiss().then(() => (isOfflineWarningToastPresent = false));
        }
      }
    };

    onMounted(() => {
      // Listen for our custom event from the SW registration
      document.addEventListener('swUpdated', updateAvailable, { once: true });

      // Prevent multiple refreshes
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        refreshing = true;
        // Here the actual reload of the page occurs
        window.location.reload();
      });

      window.addEventListener('offline', showOfflineWarning);
    });

    onUnmounted(() => {
      window.removeEventListener('offline', showOfflineWarning);
    });
  }
});
</script>